import {
  CalendarDotsIcon,
  ChatCenteredTextIcon,
  CheckSquareOffsetIcon,
  ToothIcon,
  UsersIcon,
} from '@phosphor-icons/react'
import { keys, startCase } from 'lodash'
import { createElement } from 'react'
import { HYGIENE_DUE_SOON_TAB, HYGIENE_OVERDUE_TAB } from 'app/lib/hygieneTabs'
import type { CustomPageJson, CustomPages } from 'app/types/entities'
import type { NavItemsInput, RecordTypeDef, RecordTypeKey } from 'app/types/navigation'
import { callOutcomes } from 'types/CallDataExtraction'
import type { NavItemType } from 'types/Sidebar'

export const tagsPages: Record<string, keyof typeof callOutcomes> = {
  'missed-call': `Missed Call`,
  callback: `Callback`,
  voicemail: `Voicemail`,
  'not-scheduled': `Appointment Not Scheduled`,
  canceled: `Canceled Appointment`,
  scheduled: `Appointment Scheduled`,
  rescheduled: `Appointment Rescheduled`,
  confirmation: `Appointment Confirmation`,
}

const CONTACTS_PATH = /^\/contacts\/?/
const ACTIVITIES_PATH = /^\/activities\/?/
const TASKS_PATH = /^\/tasks\/?/
// Anchored to the bare page, so it must not match /appointments-calendar.
const APPOINTMENTS_PATH = /^\/appointments\/?$/
const TREATMENT_PLANS_PATH = /^\/treatment-plans\/?$/

export const RECORD_TYPES: Readonly<Record<RecordTypeKey, RecordTypeDef>> = {
  contacts: {
    key: `contacts`,
    basePath: `/contacts`,
    customPageType: `contacts`,
    label: `Contacts`,
    icon: () => createElement(UsersIcon, { size: 20, weight: `bold` }),
    path: CONTACTS_PATH,
    gate: () => ({ product: `CRM`, permission: [`org:pages:contacts`, `pages:contacts`] }),
    all: ({ location, counts }) => ({
      name: `All`,
      href: `/contacts?tab=all`,
      active: !!location.pathname.match(CONTACTS_PATH) && location.search.includes(`tab=all`),
      count: counts.contacts?.[`all`],
    }),
    presets: ({ location, counts, hygieneEnabled, workspace }) => {
      const onPage = !!location.pathname.match(CONTACTS_PATH)
      return [
        {
          slug: `active-leads`,
          name: `Active Leads`,
          href: `/contacts`,
          active: onPage && !location.search.includes(`tab=`),
          count: counts.contacts?.[`active-leads`],
        },
        {
          slug: `scheduled-leads`,
          name: `Scheduled Leads`,
          href: `/contacts?tab=scheduled-leads`,
          active: onPage && location.search.includes(`tab=scheduled-leads`),
          count: counts.contacts?.[`scheduled-leads`],
        },
        {
          slug: `closed-lost-leads`,
          name: `Closed Lost Leads`,
          href: `/contacts?tab=closed-lost-leads`,
          active: onPage && location.search.includes(`tab=closed-lost-leads`),
          count: counts.contacts?.[`closed-lost-leads`],
        },
        ...(hygieneEnabled && workspace
          ? [
              {
                slug: HYGIENE_OVERDUE_TAB,
                name: `Hygiene Overdue`,
                href: `/contacts?tab=${HYGIENE_OVERDUE_TAB}`,
                active: onPage && location.search.includes(`tab=${HYGIENE_OVERDUE_TAB}`),
                count: counts.contacts?.[HYGIENE_OVERDUE_TAB],
              },
              {
                slug: HYGIENE_DUE_SOON_TAB,
                name: `Hygiene Due (30 Days)`,
                href: `/contacts?tab=${HYGIENE_DUE_SOON_TAB}`,
                active: onPage && location.search.includes(`tab=${HYGIENE_DUE_SOON_TAB}`),
                count: counts.contacts?.[HYGIENE_DUE_SOON_TAB],
              },
            ]
          : []),
      ]
    },
  },
  activities: {
    key: `activities`,
    basePath: `/activities`,
    customPageType: `activities`,
    label: `Activities`,
    icon: () => createElement(ChatCenteredTextIcon, { size: 20, weight: `bold` }),
    path: ACTIVITIES_PATH,
    gate: () => ({ product: `CRM`, permission: [`org:pages:activities`, `pages:activities`] }),
    all: ({ location, counts }) => ({
      name: `All`,
      href: `/activities`,
      active: !!location.pathname.match(ACTIVITIES_PATH) && !location.search.includes(`tab=`),
      count: counts.activities?.[`all`],
    }),
    presets: ({ location, counts }) =>
      keys(tagsPages).map((key) => ({
        slug: key,
        name: startCase(key),
        href: `/activities?tab=${key}`,
        active: !!location.pathname.match(ACTIVITIES_PATH) && location.search.includes(`tab=${key}`),
        count: counts.activities?.[key],
      })),
  },
  tasks: {
    key: `tasks`,
    basePath: `/tasks`,
    customPageType: `tasks`,
    label: `Tasks`,
    icon: () => createElement(CheckSquareOffsetIcon, { size: 20, weight: `bold` }),
    path: TASKS_PATH,
    gate: ({ user }) => ({ if: !!user.workspaceId, product: `CRM`, permission: [`org:pages:tasks`, `pages:tasks`] }),
    all: ({ location, counts }) => ({
      name: `All Tasks`,
      href: `/tasks?tab=all-tasks`,
      active: !!location.pathname.match(TASKS_PATH) && location.search.includes(`tab=all-tasks`),
      count: counts.tasks?.[`all-tasks`],
    }),
    presets: ({ location, counts }) => {
      const onPage = !!location.pathname.match(TASKS_PATH)
      return [
        {
          slug: `open-tasks`,
          name: `Open Tasks`,
          href: `/tasks?tab=open-tasks`,
          active: onPage && (location.search.includes(`tab=open-tasks`) || !location.search.includes(`tab=`)),
          count: counts.tasks?.[`open-tasks`],
        },
        {
          slug: `my-tasks`,
          name: `My Tasks`,
          href: `/tasks?tab=my-tasks`,
          active: onPage && location.search.includes(`tab=my-tasks`),
          count: counts.tasks?.[`my-tasks`],
          countColor: counts.hasOverdueMyTasks ? `text-red-600` : undefined,
        },
      ]
    },
  },
  treatmentPlans: {
    key: `treatmentPlans`,
    basePath: `/treatment-plans`,
    customPageType: `treatmentPlans`,
    label: `Treatment Plans`,
    icon: () => createElement(ToothIcon, { size: 20, weight: `bold` }),
    path: TREATMENT_PLANS_PATH,
    gate: ({ treatmentGate }) => ({
      product: `CRM`,
      permission: [`org:pages:contacts`, `pages:contacts`],
      feature: `action-center`,
      if: treatmentGate !== `hidden`,
      isLocked: treatmentGate === `padlock`,
    }),
    all: ({ location, counts }) => ({
      name: `All`,
      href: `/treatment-plans`,
      // On a saved list's tab its own row lights, not this one (as on Activities).
      active: TREATMENT_PLANS_PATH.test(location.pathname) && !location.search.includes(`tab=`),
      count: counts.treatmentPlans?.[`all`],
    }),
    presets: () => [],
  },
  appointments: {
    key: `appointments`,
    basePath: `/appointments`,
    customPageType: `appointments`,
    label: `Appointments`,
    icon: () => createElement(CalendarDotsIcon, { size: 20, weight: `bold` }),
    path: APPOINTMENTS_PATH,
    gate: ({ isIntegrated }) => ({
      product: `CRM`,
      permission: [`org:pages:contacts`, `pages:contacts`],
      isLocked: !isIntegrated,
    }),
    all: ({ location, counts }) => ({
      name: `All`,
      href: `/appointments`,
      // On a saved list's tab its own row lights, not this one (as on Treatment Plans).
      active: APPOINTMENTS_PATH.test(location.pathname) && !location.search.includes(`tab=`),
      count: counts.appointments?.[`upcoming`],
    }),
    presets: () => [],
  },
}

export function recordTypeRow(recordType: RecordTypeDef, input: NavItemsInput): NavItemType {
  return { ...recordType.all(input), ...recordType.gate(input), name: recordType.label, icon: recordType.icon }
}

export function customListItem(recordType: RecordTypeDef, page: CustomPageJson, input: NavItemsInput): NavItemType {
  return {
    name: startCase(page.name),
    href: `${recordType.basePath}?tab=${page.id}`,
    active: !!input.location.pathname.match(recordType.path) && input.location.search.includes(`tab=${page.id}`),
    count: input.counts[recordType.key]?.[page.id ?? ``],
  }
}
